'use client';

import { useRef, useState } from 'react';
import Input from 'src/components/primitives/Input/Input';
import {
	TRADER_PROFILE_OPTIONS,
	UserSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import SettingsSwitch from 'src/components/Settings/components/SettingsSwitch';
import { getCurrentTraderProfile } from 'src/utils/traderProfile';
import { TradeOffsetPrice } from '@velocity-exchange/common';
import { SlippageTolerance } from 'src/@types/types';
import SwiftSettings from 'src/components/Settings/TradeSettings/SwiftSettings';
import SlippageToleranceInput from 'src/components/TradeForm/SlippageToleranceInput/SlippageToleranceInput';
import { Settings } from 'src/components/Settings/SettingsForm';
import RadioGroup from 'src/components/primitives/RadioGroup/RadioGroup';
import Select from 'src/components/primitives/Select/Select';
import useSelect from 'src/components/primitives/Select/hooks/useSelect';
import Link from 'src/components/primitives/Link/Link';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import Divider from 'src/components/primitives/Divider/Divider';

interface TradeSettingsProps {
	localSettings: Settings;
	setLocalSettings: (setting: Settings) => void;
}

const START_FROM_OPTIONS: {
	label: string;
	value: TradeOffsetPrice | 'marketBased';
}[] = [
	{ label: 'Oracle Price', value: 'oracle' },
	{ label: 'Mark Price', value: 'mark' },
	{ label: 'Best Bid/Ask', value: 'bestOffer' },
	{ label: 'Est. Entry Price', value: 'entry' },
	{ label: 'Best Overall Price', value: 'best' },
	{ label: 'Market Based', value: 'marketBased' },
];

const END_FROM_OPTIONS: { label: string; value: TradeOffsetPrice }[] = [
	{ label: 'Worst', value: 'worst' },
	{ label: 'Oracle Price', value: 'oracle' },
	{ label: 'Mark Price', value: 'mark' },
	{ label: 'Best Bid/Ask', value: 'bestOffer' },
	{ label: 'Est. Entry Price', value: 'entry' },
	{ label: 'Best Overall Price', value: 'best' },
];

const MAX_DURATION = 100;
const MAX_OFFSET = 10;

const TradeSettings = ({
	localSettings,
	setLocalSettings,
}: TradeSettingsProps) => {
	const initialSettingsRef = useRef(localSettings);
	const currentSettingsRef = useRef(localSettings);

	const slippageTolerance = useRef(localSettings.slippageTolerance);

	const auctionDuration = useRef(localSettings.auctionDuration.toString());
	const auctionStartPriceOffset = useRef(
		localSettings.auctionStartPriceOffset.toString()
	);
	const auctionEndPriceOffset = useRef(
		localSettings.auctionEndPriceOffset.toString()
	);
	const auctionStartPriceOffsetFrom = useRef(
		localSettings.auctionStartPriceOffsetFrom
	);
	const auctionEndPriceOffsetFrom = useRef(
		localSettings.auctionEndPriceOffsetFrom
	);
	const oracleOffsetOrdersEnabled = useRef(
		localSettings.oracleOffsetOrdersEnabled
	);

	const placeAndTakeEnabled = useRef(localSettings.placeAndTakeEnabled);

	const traderProfile = useRef(getCurrentTraderProfile(localSettings));

	const swiftEnabled = useRef(localSettings.swiftEnabled);

	const [settingsEditable, setSettingsEditable] = useState(
		traderProfile.current === 'custom'
	);

	const updateSettingsValue = (updatedSettings: Partial<UserSettings>) => {
		const newSettings = {
			...currentSettingsRef.current,
			...updatedSettings,
		};

		setLocalSettings(newSettings);

		currentSettingsRef.current = newSettings;
	};

	const handleUpdateTradingProfile = (
		newVal: Partial<UserSettings> | undefined
	) => {
		let settingsToUse;

		if (newVal) {
			traderProfile.current = getCurrentTraderProfile(newVal);
			settingsToUse = newVal;
			setSettingsEditable(false);
		} else {
			traderProfile.current = 'custom';
			settingsToUse = initialSettingsRef.current;
			setSettingsEditable(true);
		}

		updateSettingsValue({ traderProfile: traderProfile.current });

		handleSlippageToleranceChange(settingsToUse.slippageTolerance);
		handleAuctionDurationChange(
			settingsToUse.auctionDuration?.toString() ?? ''
		);
		if (typeof settingsToUse.auctionStartPriceOffset === 'number') {
			handleAuctionStartPriceOffsetChange(
				Math.abs(settingsToUse.auctionStartPriceOffset)?.toString()
			);
		}
		handleAuctionEndPriceOffsetChange(
			settingsToUse.auctionEndPriceOffset?.toString() ?? ''
		);
		handleAuctionStartPriceOffsetFrom(
			settingsToUse.auctionStartPriceOffsetFrom as
				| TradeOffsetPrice
				| 'marketBased'
		);
		handleAuctionEndPriceOffsetFrom(
			settingsToUse.auctionEndPriceOffsetFrom as TradeOffsetPrice
		);
		handleToggleOracleOrders(settingsToUse.oracleOffsetOrdersEnabled);
		handleTogglePlaceAndTake(settingsToUse.placeAndTakeEnabled);
	};

	const handleSlippageToleranceChange = (
		newVal: SlippageTolerance | undefined
	) => {
		if (!newVal) {
			return;
		}

		updateSettingsValue({
			slippageTolerance: newVal,
		});

		slippageTolerance.current = newVal;
	};

	const handleAuctionDurationChange = (newVal: string) => {
		if (newVal?.length > 0 && !isNaN(Number(newVal)) && Number(newVal) >= 0) {
			if (Number(newVal) >= MAX_DURATION) {
				newVal = MAX_DURATION.toString();
			}
			updateSettingsValue({ auctionDuration: Number(newVal) });
		}

		auctionDuration.current = newVal;
	};

	const handleAuctionStartPriceOffsetChange = (newVal: string) => {
		if (newVal?.length > 0 && !isNaN(Number(newVal)) && Number(newVal) >= 0) {
			if (Math.abs(Number(newVal)) >= MAX_OFFSET) {
				newVal = MAX_OFFSET.toString();
			}
			updateSettingsValue({ auctionStartPriceOffset: -Number(newVal) });
		}

		auctionStartPriceOffset.current = newVal;
	};

	const handleAuctionEndPriceOffsetChange = (newVal: string) => {
		if (newVal?.length > 0 && !isNaN(Number(newVal)) && Number(newVal) >= 0) {
			if (Math.abs(Number(newVal)) >= MAX_OFFSET) {
				newVal = MAX_OFFSET.toString();
			}
			updateSettingsValue({ auctionEndPriceOffset: Number(newVal) });
		}
		auctionEndPriceOffset.current = newVal;
	};

	const handleAuctionStartPriceOffsetFrom = (
		newVal: TradeOffsetPrice | 'marketBased'
	) => {
		updateSettingsValue({ auctionStartPriceOffsetFrom: newVal });
		auctionStartPriceOffsetFrom.current = newVal;
	};

	const handleAuctionEndPriceOffsetFrom = (newVal: TradeOffsetPrice) => {
		updateSettingsValue({ auctionEndPriceOffsetFrom: newVal });
		auctionEndPriceOffsetFrom.current = newVal;
	};

	const handleToggleOracleOrders = (val?: boolean) => {
		const newVal = val !== undefined ? val : !oracleOffsetOrdersEnabled.current;

		updateSettingsValue({
			oracleOffsetOrdersEnabled: newVal,
		});
		oracleOffsetOrdersEnabled.current = newVal;
	};

	const handleTogglePlaceAndTake = (val?: boolean) => {
		const newVal = val !== undefined ? val : !placeAndTakeEnabled.current;

		updateSettingsValue({
			placeAndTakeEnabled: newVal,
		});
		placeAndTakeEnabled.current = newVal;
	};

	const handleToggleSwift = (val?: boolean) => {
		const newVal = val !== undefined ? val : !swiftEnabled.current;

		updateSettingsValue({
			swiftEnabled: newVal,
		});
		swiftEnabled.current = newVal;
	};

	const auctionStartPrice = (() => {
		if (auctionStartPriceOffsetFrom.current === 'marketBased') {
			return 'Market Based';
		}

		const startPriceFrom = START_FROM_OPTIONS.find(
			(option) => option.value === auctionStartPriceOffsetFrom.current
		)?.label;

		const startPriceTo = Math.abs(Number(auctionStartPriceOffset.current));

		return `${startPriceFrom} - ${startPriceTo}%`;
	})();

	const {
		selectOptions: actionStartSelectOptions,
		selectedOption: actionStartSelectedOption,
		value: actionStartSelectValue,
		onValueChange: actionStartSelectOnValueChange,
		open: actionStartSelectOpen,
		onOpenChange: actionStartSelectOnOpenChange,
	} = useSelect({
		options: START_FROM_OPTIONS,
		selectedValue: auctionStartPriceOffsetFrom.current,
		onSelectedValueChange: handleAuctionStartPriceOffsetFrom,
	});

	const {
		selectOptions: actionEndSelectOptions,
		selectedOption: actionEndSelectedOption,
		value: actionEndSelectValue,
		onValueChange: actionEndSelectOnValueChange,
		open: actionEndSelectOpen,
		onOpenChange: actionEndSelectOnOpenChange,
	} = useSelect({
		options: END_FROM_OPTIONS,
		selectedValue: auctionEndPriceOffsetFrom.current,
		onSelectedValueChange: handleAuctionEndPriceOffsetFrom,
	});

	return (
		<div className="flex flex-col gap-y-2xl text-text-default">
			<div className="flex flex-col gap-y-sm">
				<h2 className="typo-label-sm">Trader Profile</h2>
				<RadioGroup.Root
					value={traderProfile.current}
					onValueChange={(value) =>
						handleUpdateTradingProfile(
							TRADER_PROFILE_OPTIONS.find((option) => option.key === value)
								?.value
						)
					}
					className="grid grid-cols-2 gap-md"
				>
					{TRADER_PROFILE_OPTIONS.map((option) => (
						<RadioGroup.Item
							id={option.key}
							size="md"
							variant="card"
							layout="stacked"
							key={option.key}
							value={option.key}
							label={option.label}
							subLabel={option.description}
						/>
					))}
				</RadioGroup.Root>
			</div>

			{settingsEditable ? (
				<>
					<div className="flex flex-col gap-y-sm">
						<h3 className="typo-label-sm">Default Slippage Tolerance</h3>
						<span className="typo-body-xs text-text-secondary">
							Maximum slippage tolerated for every order
						</span>
						<SlippageToleranceInput
							className="gap-x-sm sm:gap-x-md"
							optionsClassName="gap-x-sm sm:gap-md"
							updateHandler={handleSlippageToleranceChange}
							slippageTolerance={slippageTolerance.current}
						/>
					</div>
					<Divider />
					<div className="flex flex-col gap-y-sm">
						<h3 className="flex items-center gap-x-sm typo-label-sm">
							Order Auction Parameters
						</h3>
						<span className="typo-body-xs text-text-secondary">
							Customize how orders are executed. These settings apply to all
							Market orders and Limit orders that cross the spread.{' '}
							<Link
								href="https://docs.drift.trade/trading/auction-parameters"
								openInNewTab
							>
								Learn more
							</Link>
						</span>
						<div className="flex items-center justify-between gap-x-md typo-body-xs">
							<DefinitionTooltip
								className="shrink-0"
								definitionId="settingsDuration"
							>
								Duration
							</DefinitionTooltip>
							<div className="flex items-center gap-x-sm">
								<span>
									{isNaN(Number(auctionDuration.current))
										? 0
										: Number(auctionDuration.current) / 2}{' '}
									sec
								</span>
								<div className="w-[110px]">
									<Input
										id="auction-duration"
										labelVariant="inset"
										label="Slots"
										type="number"
										onChange={handleAuctionDurationChange}
										value={auctionDuration.current}
									/>
								</div>
							</div>
						</div>
					</div>
					<div className="flex items-center justify-between gap-x-md typo-body-xs">
						<Tooltip
							className="shrink-0"
							content={`Start price for auctions will be ${
								START_FROM_OPTIONS.find(
									(option) =>
										option.value === auctionStartPriceOffsetFrom.current
								)?.label
							} with a ${`${
								auctionStartPriceOffset.current === 'marketBased'
									? 'Market Based'
									: `${Math.abs(
											Number(auctionStartPriceOffset.current ?? '0')
										).toString()}%`
							}`} discount.`}
						>
							Start Price
						</Tooltip>

						<div className="flex items-center gap-x-sm">
							<Select.Root
								open={actionStartSelectOpen}
								onOpenChange={actionStartSelectOnOpenChange}
								value={actionStartSelectValue}
								onValueChange={actionStartSelectOnValueChange}
							>
								<Select.Trigger>
									{actionStartSelectedOption?.label}
								</Select.Trigger>
								<Select.Options>
									{actionStartSelectOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>

							<div className="w-[100px]">
								<Input
									labelVariant="none"
									type="number"
									onChange={handleAuctionStartPriceOffsetChange}
									value={auctionStartPriceOffset.current?.replace('-', '')}
									suffix="%"
								/>
							</div>
						</div>
					</div>
					<div className="flex items-center justify-between gap-x-md typo-body-xs">
						<Tooltip
							className="shrink-0"
							content={
								<>
									<p>
										End price for auctions will be{' '}
										{
											END_FROM_OPTIONS.find(
												(option) =>
													option.value === auctionEndPriceOffsetFrom.current
											)?.label
										}{' '}
										with a $
										{auctionEndPriceOffset.current === 'marketBased'
											? 'Market Based'
											: `${Math.abs(
													Number(auctionEndPriceOffset.current ?? '0')
												).toString()}%`}{' '}
										premium.
									</p>
									<p>
										If end price is better than start price at the time of
										order, start price plus one tick will be used as the end
										price.
									</p>
								</>
							}
						>
							End Price
						</Tooltip>

						<div className="flex items-center gap-x-sm">
							<Select.Root
								open={actionEndSelectOpen}
								onOpenChange={actionEndSelectOnOpenChange}
								value={actionEndSelectValue}
								onValueChange={actionEndSelectOnValueChange}
							>
								<Select.Trigger>
									{actionEndSelectedOption?.label}
								</Select.Trigger>
								<Select.Options>
									{actionEndSelectOptions.map((option) => (
										<Select.Option key={option.value} value={option.value}>
											{option.label}
										</Select.Option>
									))}
								</Select.Options>
							</Select.Root>

							<div className="w-[100px]">
								<Input
									labelVariant="none"
									type="number"
									onChange={handleAuctionEndPriceOffsetChange}
									value={auctionEndPriceOffset.current}
									suffix="%"
								/>
							</div>
						</div>
					</div>
					<div>
						<SettingsSwitch
							key="oracle_offset_toggle"
							label="Use Oracle Auctions"
							checked={oracleOffsetOrdersEnabled.current}
							onChange={handleToggleOracleOrders}
							tooltipDefinitionId="settingsOracleAuctions"
						/>
					</div>
					<div>
						<SettingsSwitch
							key="place_and_take_toggle"
							label="Use Place And Take"
							checked={placeAndTakeEnabled.current}
							onChange={handleTogglePlaceAndTake}
							tooltipDefinitionId="settingsPlaceAndTake"
						/>
					</div>
				</>
			) : (
				/* Fixed settings view for trader profile preset */
				<div>
					<div className="flex w-full flex-col gap-y-sm">
						<div className="flex w-full items-center justify-between gap-x-lg">
							<span className="typo-body-xs">Slippage</span>
							<span className="typo-body-xs text-text-default">
								{slippageTolerance.current === 'dynamic'
									? `Dynamic`
									: `${slippageTolerance.current}%`}
							</span>
						</div>
						<div className="flex w-full items-center justify-between gap-x-lg">
							<span className="typo-body-xs">Auction duration</span>
							<span className="typo-body-xs text-text-default">
								~
								{isNaN(Number(auctionDuration.current))
									? 0
									: Number(auctionDuration.current) / 2}{' '}
								sec
							</span>
						</div>
						<div className="flex w-full items-center justify-between gap-x-lg">
							<span className="typo-body-xs">Auctions will be based on</span>
							<span className="typo-body-xs text-text-default">
								{oracleOffsetOrdersEnabled.current
									? 'Oracle Price'
									: 'Market Price'}
							</span>
						</div>
						<div className="flex w-full items-center justify-between gap-x-lg">
							<span className="typo-body-xs">Auction start price</span>
							<span className="typo-body-xs text-text-default">
								{auctionStartPrice}
							</span>
						</div>
						<div className="flex w-full items-center justify-between gap-x-lg">
							<span className="typo-body-xs">Auction end price</span>
							<span className="typo-body-xs text-text-default">
								Worst Overall Price +{' '}
								{Math.abs(Number(auctionEndPriceOffset.current))}%
							</span>
						</div>
						<div className="flex w-full items-center justify-between gap-x-lg">
							<span className="typo-body-xs">
								Fill against resting liquidity first
							</span>
							<span className="typo-body-xs text-text-default">
								{placeAndTakeEnabled.current ? 'Yes' : 'No'}
							</span>
						</div>
					</div>
				</div>
			)}
			<Divider />
			<SwiftSettings
				toggleState={swiftEnabled.current}
				onToggleStateChange={handleToggleSwift}
			/>
		</div>
	);
};

export default TradeSettings;
